import type { Map as MapboxMap } from 'mapbox-gl';
import {
    MARKER, CONFLICT_MARKER, CONFLICT_COLOR, MAP_THEME, MAP_BEHAVIOR, MAP_LAYERS, MAP_SOURCE_ID, buildMarkerSvg,
} from './components/mapTheme';
import { DEAL_GROUP_PALETTE } from '../constants';

export type MarkerImage = [imageId: string, image: HTMLImageElement];

const TERRAIN_SOURCE_ID = 'mapbox-dem';
const COMPOSITE_SOURCE_ID = 'composite';
const TERRAIN_EXAGGERATION = 1.5;

const loadSvgAsImage = (svgString: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
});

// Static SVGs: decode once and reuse across style rebuilds; a failed decode clears the cache so the next rebuild retries.
let markerImagesPromise: Promise<MarkerImage[]> | null = null;

/** All deal marker bitmaps as [imageId, image] pairs. */
export const loadMarkerImages = (): Promise<MarkerImage[]> => {
    if (!markerImagesPromise) {
        markerImagesPromise = Promise.all([
            loadSvgAsImage(MARKER).then((img): MarkerImage => ['marker', img]),
            loadSvgAsImage(CONFLICT_MARKER).then((img): MarkerImage => ['marker-conflict', img]),
            ...DEAL_GROUP_PALETTE.map((entry, idx) => loadSvgAsImage(buildMarkerSvg(entry.hex, 'white'))
                .then((img): MarkerImage => [`marker-${idx}`, img])),
        ]);
        markerImagesPromise.catch(() => { markerImagesPromise = null; });
    }
    return markerImagesPromise;
};

const addMarkerImages = (map: MapboxMap, markerImages: MarkerImage[]): void => {
    markerImages.forEach(([imageId, img]) => {
        if (!map.hasImage(imageId)) map.addImage(imageId, img, { sdf: false });
    });
};

const addTerrain = (map: MapboxMap): void => {
    if (!map.getSource(TERRAIN_SOURCE_ID)) {
        map.addSource(TERRAIN_SOURCE_ID, {
            type: 'raster-dem',
            url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
            tileSize: 512,
            maxzoom: MAP_BEHAVIOR.maxZoom,
        });
    }
    map.setTerrain({ source: TERRAIN_SOURCE_ID, exaggeration: TERRAIN_EXAGGERATION });
};

const addBuildings = (map: MapboxMap): void => {
    // Satellite-only styles have no vector 'composite' source to extrude buildings from.
    if (!map.getSource(COMPOSITE_SOURCE_ID) || map.getLayer(MAP_LAYERS.buildings)) return;

    const labelLayerId = map.getStyle().layers?.find(
        layer => layer.type === 'symbol' && layer.layout?.['text-field'],
    )?.id;

    map.addLayer(
        {
            id: MAP_LAYERS.buildings,
            source: COMPOSITE_SOURCE_ID,
            'source-layer': 'building',
            filter: ['==', 'extrude', 'true'],
            type: 'fill-extrusion',
            minzoom: MAP_BEHAVIOR.minZoom,
            paint: {
                'fill-extrusion-color': '#aaa',
                'fill-extrusion-height': ['get', 'height'],
                'fill-extrusion-base': ['get', 'min_height'],
                'fill-extrusion-opacity': 0.5,
            },
        },
        labelLayerId,
    );
};

const addDealsSource = (map: MapboxMap, geojson: GeoJSON.FeatureCollection<GeoJSON.Point>): void => {
    if (map.getSource(MAP_SOURCE_ID)) return;

    const clusterProperties: Record<string, unknown> = {};
    DEAL_GROUP_PALETTE.forEach((_, idx) => {
        clusterProperties[`groupColor_${idx}`] = ['+', ['get', `groupColor_${idx}`]];
    });

    map.addSource(MAP_SOURCE_ID, {
        type: 'geojson',
        data: geojson,
        cluster: true,
        clusterMaxZoom: MAP_BEHAVIOR.clusterMaxZoom,
        clusterRadius: MAP_THEME.clusterCircleRadius,
        clusterProperties,
    });
};

const addClusterLayers = (map: MapboxMap): void => {
    if (!map.getLayer(MAP_LAYERS.clusters)) {
        map.addLayer({
            id: MAP_LAYERS.clusters,
            type: 'circle',
            source: MAP_SOURCE_ID,
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': [
                    'case',
                    ...DEAL_GROUP_PALETTE.map((entry, idx) => [
                        ['>', ['get', `groupColor_${idx}`], 0],
                        entry.hex,
                    ]).flat(),
                    MAP_THEME.clusterColor,
                ],
                'circle-radius': MAP_THEME.clusterCircleRadius,
            },
        });
    }

    if (!map.getLayer(MAP_LAYERS.clusterCount)) {
        map.addLayer({
            id: MAP_LAYERS.clusterCount,
            type: 'symbol',
            source: MAP_SOURCE_ID,
            filter: ['has', 'point_count'],
            layout: {
                'text-field': '{point_count_abbreviated}',
                'text-size': MAP_THEME.clusterCountFontSize,
            },
        });
    }
};

const addPinLayer = (map: MapboxMap): void => {
    if (map.getLayer(MAP_LAYERS.unclusteredPoint)) return;

    map.addLayer({
        id: MAP_LAYERS.unclusteredPoint,
        type: 'symbol',
        source: MAP_SOURCE_ID,
        filter: ['!', ['has', 'point_count']],
        layout: {
            'icon-image': [
                'case',
                ['==', ['get', 'groupColorIdx'], -1],
                'marker',
                ['==', ['get', 'colorHex'], CONFLICT_COLOR],
                'marker-conflict',
                ['concat', 'marker-', ['to-string', ['get', 'groupColorIdx']]],
            ],
            'icon-size': 1,
            'icon-anchor': 'bottom',
            'icon-allow-overlap': true,
        },
        paint: {
            'icon-opacity': 1,
        },
    });
};

/** Re-adds everything a style rebuild wipes: marker images, terrain, buildings, and the clustered deals source and layers. */
export const installRuntimeLayers = (
    map: MapboxMap,
    markerImages: MarkerImage[],
    geojson: GeoJSON.FeatureCollection<GeoJSON.Point>,
): void => {
    addMarkerImages(map, markerImages);
    addTerrain(map);
    addBuildings(map);
    addDealsSource(map, geojson);
    addClusterLayers(map);
    addPinLayer(map);
};

/** Dims and resizes pins and clusters to spotlight the selected and hovered deals. */
export const applySelectionEmphasis = (map: MapboxMap, selectedDealId: number | null, hoveredDealId: number | null): void => {
    if (!map.getLayer(MAP_LAYERS.unclusteredPoint)) return;
    const hasSelection = selectedDealId != null;

    if (selectedDealId != null) {
        map.setLayoutProperty(MAP_LAYERS.unclusteredPoint, 'icon-size', [
            'case',
            ['==', ['get', 'id'], selectedDealId], MAP_THEME.selectedIconSize,
            ['==', ['get', 'id'], hoveredDealId ?? -1], MAP_THEME.hoveredIconSize,
            1,
        ]);

        map.setPaintProperty(MAP_LAYERS.unclusteredPoint, 'icon-opacity', [
            'case',
            ['==', ['get', 'id'], selectedDealId], 1,
            ['==', ['get', 'id'], hoveredDealId ?? -1], MAP_THEME.hoveredOpacity,
            MAP_THEME.nonSelectedOpacity,
        ]);
    } else {
        map.setLayoutProperty(MAP_LAYERS.unclusteredPoint, 'icon-size', [
            'case',
            ['==', ['get', 'id'], hoveredDealId ?? -1], MAP_THEME.hoveredIconSize,
            1,
        ]);

        map.setPaintProperty(MAP_LAYERS.unclusteredPoint, 'icon-opacity', 1);
    }

    if (map.getLayer(MAP_LAYERS.clusters)) {
        map.setPaintProperty(MAP_LAYERS.clusters, 'circle-opacity', hasSelection ? MAP_THEME.nonSelectedOpacity : 1);
    }
    if (map.getLayer(MAP_LAYERS.clusterCount)) {
        map.setPaintProperty(MAP_LAYERS.clusterCount, 'text-opacity', hasSelection ? MAP_THEME.nonSelectedOpacity : 1);
    }
};
